import * as Sentry from "@sentry/react";
import classnames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { default as InlineSVG, default as SVG } from "react-inlinesvg";
import { usePopper } from "react-popper";
import Utils, { countWords, event, formattedDiff, hash, markdownFormatter, Tip } from "../Utils";
import { isMuse2AlphaModelLabel } from "../components/WriteSettings/muse2Alpha";
import { stripPassageTags } from "~shared/stripPassageTags";
import ConfirmModal from "../components/ConfirmModal";
import {
  buildClipboardPayload,
  buildMarkdownClipboardPayload,
  buildSimpleHtmlFromText,
  copyHtmlToClipboard,
  normalizeText,
  processMarkdownCardForInsertion,
} from "../components/PersistentChat/copyUtils";
import { TourSignal } from "../components/StickyTour/TourTypes";
import useClickedOutside from "../hooks/useClickedOutside";
import { useStickyTourStore } from "../stores/stickyTourStore";
import { Textarea } from "../sudokit/components/Textarea";
import { convertQuotes } from "../lib/convertQuotes";
import { CardViewHeader, CardViewOptions, DeleteButton } from "./styles";

// A single card in a card set
const CardView = React.memo(function CardView(props) {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  // const [flagOpen, setFlagOpen] = useState(false);
  // formats a completion to look nice
  let format = (text) => {
    let out = text;

    // replace double newlines and tabs with just one
    //out = out.replace(/\n\s*\n/g, '\n');
    out = out.replace(/\t\s*\t/g, "\t");
    out = out.replace(/\t/g, "\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0\u00a0");

    return out;
  };

  let showFav = !props.disableFav;
  let showLikes = !props.disableLikes;

  // checks in the favoritesbycards hash whether this completion has been faved

  let faved = false;
  if (props.favoritesByCards[props.card.id]) {
    faved = props.favoritesByCards[props.card.id][hash(props.content)];
  }

  const { content: contentFromProps, boldFirstSentence, formatter, formatMarkdown = false, compareOriginal } = props;
  // Only chapterGenerator (Draft) cards hide action buttons during streaming
  // and while empty — other card types keep their existing always-visible
  // behavior (SW-9212).
  const isChapterGenerator = props.card?.data?.source === "chapterGenerator";
  const hasContent = typeof contentFromProps === "string" && contentFromProps.trim().length > 0;
  const showActionButtons = !isChapterGenerator || (props.doneStreaming && hasContent);

  const content = useMemo(() => {
    let content = contentFromProps.trim();

    // Convert straight quotes to match document style for display
    if (props.editor) {
      content = convertQuotes(content, props.editor);
    }

    if (boldFirstSentence && content && content.length != 0) {
      // bold only the first sentence and separate the rest below gapped w/ newline
      let re = /^(.*?[\.!\?])(.*$)/;

      let matches = content.match(re);

      if (matches) {
        let first = matches[1];
        let rest = matches[2];

        content = (
          <>
            {first && <strong>{Utils.capitalizeFirstLetter(first)}</strong>}
            {rest && (
              <>
                <br />
                <br />
                {Utils.nl2br(rest)}
              </>
            )}
          </>
        );
      } else {
        content = Utils.nl2br(content);
      }
    } else if (formatter) {
      content = formatter(content);
    } else if (compareOriginal) {
      // if compareOriginal is passed in then we show a diff format
      content = formattedDiff(compareOriginal, format(content));
    } else if (formatMarkdown) {
      content = markdownFormatter(content);
    } else {
      content = format(content);
      content = Utils.nl2br(content);
    }
    return content;
  }, [boldFirstSentence, compareOriginal, contentFromProps, formatter, props.editor]);

  // Liking logic
  const [liking, setLiking] = useState(false);
  const [wiggle, setWiggle] = useState(false);
  let liked = props.card.hasLiked([props.pathKey, props.pathIndex]);

  // Muse 2 alpha cards keep the note visible and editable after saving, so
  // testers can use it as commentary to refer back to; every other card keeps
  // the original submit-once behavior.
  const isMuse2AlphaCard = isMuse2AlphaModelLabel(props.card.data?.chiclets?.model);

  const [userFeedback, setUserFeedback] = useState(props.card.getUserFeedback([props.pathKey, props.pathIndex]) || "");
  // On alpha cards the note stays rendered after submit, so the send arrow only
  // appears while there is something new to send — text that differs from the
  // saved note. It reappears on edit and vanishes again the moment submit
  // lands (setUserFeedback updates the card's local data synchronously).
  const savedUserFeedback = props.card.getUserFeedback([props.pathKey, props.pathIndex]) || "";
  const showFeedbackSend = !isMuse2AlphaCard || userFeedback !== savedUserFeedback;
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false); // Only show thank you for fresh submissions
  const [showThankYou, setShowThankYou] = useState(false);
  const [fadeThankYou, setFadeThankYou] = useState(false);
  const feedbackRef = useRef();
  const thankYouTimeoutRef = useRef();

  const handleFeedbackSubmit = useCallback(() => {
    if (userFeedback.trim()) {
      props.card.setUserFeedback([props.pathKey, props.pathIndex], userFeedback);
      setFeedbackSubmitted(true);
      setShowThankYou(true);
      setFadeThankYou(false);

      // Clear any existing timeout
      if (thankYouTimeoutRef.current) {
        clearTimeout(thankYouTimeoutRef.current);
      }

      // Start fade out after 5 seconds
      thankYouTimeoutRef.current = setTimeout(() => {
        setFadeThankYou(true);
        // Hide completely after fade transition completes
        setTimeout(() => {
          setShowThankYou(false);
          setFeedbackSubmitted(false);
        }, 300);
      }, 5000);

      event({
        eventType: "feedback-submit",
        cardId: props.card.id,
        path: [props.pathKey, props.pathIndex],
      });
    }
  }, [userFeedback, props.card, props.pathKey, props.pathIndex]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (thankYouTimeoutRef.current) {
        clearTimeout(thankYouTimeoutRef.current);
      }
    };
  }, []);

  // Focus the note field only when the user just clicked a thumb — a liked
  // transition, not a mount. On mount `liked` may already be set (saved thumbs
  // on reload; alpha cards keep their note field rendered), and focusing then
  // would steal focus from the editor and scroll the right rail into view.
  const prevLikedRef = useRef(liked);
  useEffect(() => {
    const prevLiked = prevLikedRef.current;
    prevLikedRef.current = liked;
    if (liked === prevLiked) return;
    if ((liked === 1 || liked === -1) && feedbackRef.current) {
      feedbackRef.current.focus();
    }
  }, [liked]);

  // prevents liking multiple times while requests in flight
  let ifNotLiking = (wiggle, callback) => {
    if (!liking) {
      setLiking(true);

      if (wiggle) {
        // Wiggle animation
        setWiggle(true);
        setTimeout(() => {
          setWiggle(false);
        }, 1000);
      }

      callback().then(() => {
        setLiking(false);
      });
    }
  };

  // Copying
  const [copied, setCopied] = useState(false);

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  const handleToggleOptions = () => setIsOptionsOpen(prev => !prev);
  const handleCloseOptions = () => setIsOptionsOpen(false);

  const optionsContainerRef = useClickedOutside(handleCloseOptions);

  useEffect(() => {
    if (!isOptionsOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleCloseOptions();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOptionsOpen]);

  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const justOpenedConfirmRef = useRef(false);

  const handleDeleteItem = async () => {
    justOpenedConfirmRef.current = true;
    setIsConfirmDeleteModalOpen(true);
    // Reset after the current event loop — this ensures both pointerdown and mousedown
    // from the triggering click are ignored by the click-outside handler
    requestAnimationFrame(() => {
      justOpenedConfirmRef.current = false;
    });
  };

  const handleDeleteConfirm = async () => {
    props.onDeleteCardItem();
    setIsConfirmDeleteModalOpen(false);
  };

  const handleDeleteCancel = useCallback(() => {
    if (justOpenedConfirmRef.current) return;
    setIsConfirmDeleteModalOpen(false);
  }, []);

  const [confirmModal, setConfirmModal] = useState(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const { styles: modalStyles, attributes: modalAttributes } = usePopper(containerRef.current, confirmModal, {
    placement: "top",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 10],
        },
      },
    ],
  });

  const confirmModalClickOutsideRef = useClickedOutside(handleDeleteCancel);

  const cloneRangesToHtml = useCallback((ranges) => {
    const container = document.createElement("div");
    ranges.forEach((range) => {
      try {
        container.appendChild(range.cloneContents());
      } catch (_e) {
        // ignore invalid range clones
      }
    });
    return container.innerHTML;
  }, []);

  const buildRawHtmlFromNode = useCallback(
    (node) => {
      if (!node) return "";
      try {
        const range = (node.ownerDocument || document).createRange();
        range.selectNodeContents(node);
        return cloneRangesToHtml([range]);
      } catch (_e) {
        return node.innerHTML || "";
      }
    },
    [cloneRangesToHtml],
  );

  return (
    <div
      onCopy={(e) => {
        const selection = document.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const ranges = [];
        for (let i = 0; i < selection.rangeCount; i += 1) {
          ranges.push(selection.getRangeAt(i));
        }
        const rawHtml = cloneRangesToHtml(ranges);

        // Use markdown-specific payload builder for markdown cards
        const { plain, htmlForPaste } = formatMarkdown ? buildMarkdownClipboardPayload(rawHtml, selection.toString()) : buildClipboardPayload(rawHtml, selection.toString());

        if (plain) e.clipboardData.setData("text/plain", plain);
        if (htmlForPaste) e.clipboardData.setData("text/html", htmlForPaste);
        e.preventDefault();

        if (plain.length > 0) {
          // event for copies
          event({
            eventType: "select-copy",
            cardId: props.card.id,
            path: [props.pathKey, props.pathIndex],
          });
        }
      }}
      className={classnames({
        card: true,
        border: props.border,
        wiggle: wiggle,
      })}
    >
      <CardViewHeader ref={containerRef}>
        <span className="title">{props.title ? props.title : ""}</span>
        {!props.card.data.demo && showActionButtons && (
          <CardViewOptions ref={optionsContainerRef}>
            <div
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleToggleOptions}
              className="trigger"
            >
              <InlineSVG
                src="/assets/hotdog-menu.svg"
                height={20}
                width={20}
              />
            </div>
            {isOptionsOpen && (
              <div
                onClick={handleCloseOptions}
                className="options"
              >
                <DeleteButton
                  src="/assets/delete.svg"
                  onClick={handleDeleteItem}
                >
                  Delete
                </DeleteButton>
              </div>
            )}
          </CardViewOptions>
        )}
      </CardViewHeader>

      <div ref={contentRef}>{content}</div>

      {!props.hideDivider && <div className="divider-line"></div>}

      <div className={classnames({ extra_bottom: props.hideDivider, bottom_menu: true })}>
        {!(props.card.data.demo && props.card.data.source == "description") && showActionButtons && (
          <a
            className={classnames({
              insert: true,
              highlight: props.highlightInsert,
            })}
            type="button"
            data-before={countWords(props.content)}
            onClick={async (e) => {
              sendFlowSignal(TourSignal.INSERT_CLICKED);

              // MARKDOWN CARDS ONLY - special handling for formatted markdown
              if (formatMarkdown && contentRef.current) {
                try {
                  const { htmlForPaste } = processMarkdownCardForInsertion(contentRef.current.innerHTML);
                  props.insertEditor({ html: stripPassageTags(htmlForPaste) });
                } catch (error) {
                  Sentry.captureException(error);
                  // Fallback for markdown cards on error
                  props.insertEditor({ text: stripPassageTags(contentRef.current.innerText || props.content) });
                }
              } else {
                // NON-MARKDOWN CARDS - use plain content as-is
                props.insertEditor({ text: stripPassageTags(props.content), draftAppend: isChapterGenerator });
              }

              event({
                eventType: "insert",
                cardId: props.card.id,
                path: [props.pathKey, props.pathIndex],
              });
            }}
          >
            {props.highlightInsert && <Tip position="right">Just tap this!</Tip>}
            <SVG src="/assets/arrow.backward.circle.svg" />
            <span>Insert</span>
          </a>
        )}

        {!props.card.data.demo && showActionButtons && (
          <a
            className="copy"
            data-before={countWords(props.content)}
            type="button"
            onClick={async (e) => {
              const rawHtml = buildRawHtmlFromNode(contentRef.current);
              const textFallback = contentRef.current?.innerText || props.content;

              // Use markdown-specific payload builder for markdown cards
              const { plain, htmlForPaste } = formatMarkdown ? buildMarkdownClipboardPayload(rawHtml, textFallback) : buildClipboardPayload(rawHtml, textFallback);

              const plainText = plain || normalizeText(textFallback);
              const html = htmlForPaste || buildSimpleHtmlFromText(plainText);

              try {
                await copyHtmlToClipboard(html, plainText);
              } catch (_error) {
                if (plainText && typeof navigator !== "undefined" && navigator?.clipboard?.writeText) {
                  await navigator.clipboard.writeText(plainText);
                }
              }

              setCopied(true);
              setTimeout(() => {
                setCopied(false);
              }, 1000);
              event({
                eventType: "copy",
                cardId: props.card.id,
                path: [props.pathKey, props.pathIndex],
              });
            }}
          >
            <SVG src="/assets/docondoc.svg" />
            <span>{copied ? "Copied" : "Copy"}</span>
          </a>
        )}

        {props.open && showActionButtons && (
          <a
            className="open"
            onClick={(e) => {
              props.open(props.card.id);
            }}
          >
            <SVG src="/assets/expand.svg" />
            <span>Open</span>
          </a>
        )}
        {!props.card.data.demo && props.doneStreaming && (
          <div className="card-action-menu">
            {showLikes && (
              <>
                {/* thumbs down */}
                {liked === -1 && (
                  <a
                    href="#"
                    className="like"
                    aria-label="Thumbs Down Selected"
                    onClick={(e) => {
                      e.preventDefault();
                      ifNotLiking(false, () => {
                        return props.card.removeLike([props.pathKey, props.pathIndex]);
                      });
                    }}
                  >
                    <SVG
                      aria-hidden="true"
                      src="/assets/thumbsdown-filled.svg"
                    />
                  </a>
                )}

                {(liked === null || liked === 1) && (
                  <a
                    href="#"
                    className="like"
                    aria-label="Give this a Thumbs Down"
                    onClick={(e) => {
                      e.preventDefault();
                      ifNotLiking(false, () => {
                        return props.card.createLike([props.pathKey, props.pathIndex], -1);
                      });
                    }}
                  >
                    <SVG
                      aria-hidden="true"
                      src="/assets/thumbsdown.svg"
                    />
                  </a>
                )}

                {/* thumbs up*/}
                {liked === 1 && (
                  <a
                    href="#"
                    className="like on"
                    aria-label="Thumbs Up Selected"
                    onClick={(e) => {
                      e.preventDefault();
                      ifNotLiking(false, () => {
                        return props.card.removeLike([props.pathKey, props.pathIndex]);
                      });
                    }}
                  >
                    <SVG
                      aria-hidden="true"
                      src="/assets/thumbsup-filled.svg"
                    />
                  </a>
                )}

                {(liked === null || liked === -1) && (
                  <a
                    href="#"
                    aria-label="Give this a Thumbs Up"
                    className="like"
                    onClick={(e) => {
                      e.preventDefault();
                      ifNotLiking(false, () => {
                        return props.card.createLike([props.pathKey, props.pathIndex], 1);
                      });
                    }}
                  >
                    <SVG
                      aria-hidden="true"
                      src="/assets/thumbsup.svg"
                    />
                  </a>
                )}
              </>
            )}
            {/* star */}
            {showFav && (
              <>
                {!faved && (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      props.onFavorite(props.card, props.content);
                      event({
                        eventType: "favorite",
                        cardId: props.card.id,
                        path: [props.pathKey, props.pathIndex],
                      });
                    }}
                    className="favourite"
                    aria-label="Add this card to favorites"
                  >
                    <SVG src="/assets/star.svg" />
                  </a>
                )}

                {faved && (
                  <a
                    href="#"
                    className="faved"
                    alt="Unfavorite"
                    onClick={(e) => {
                      e.preventDefault();
                      props.onUnfavorite(props.card, props.content);
                    }}
                    aria-label="This card is Favorited"
                  >
                    <SVG src="/assets/star.fill.svg" />
                  </a>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* Simple conditional rendering - accept natural layout changes */}
      {(liked === 1 || liked === -1) && feedbackSubmitted && showThankYou && (
        <div className="!w-[calc(100%+10px)] mt-2.5">
          <div
            className={`relative left-[-5px] bottom-[-3px] rounded-[4px_4px_10px_10px] p-2 box-border border-[1.5px] border-transparent text-[15px] text-app-primary-cta flex items-center gap-1.5 transition-opacity duration-300 ${
              fadeThankYou ? "opacity-0" : "opacity-100"
            }`}
          >
            <SVG
              src="/assets/huzzah.svg"
              className="w-25 h-25 [&_path]:fill-app-primary-cta translate-y-[-3px]"
            />
            <span className="translate-y-[-3px]">Thank you!</span>
          </div>
        </div>
      )}

      {(liked === 1 || liked === -1) && (isMuse2AlphaCard || (!props.card.getUserFeedback([props.pathKey, props.pathIndex]) && !feedbackSubmitted)) && (
        <div className="!w-[calc(100%+10px)] mt-2.5">
          <Textarea
            ref={feedbackRef}
            value={userFeedback}
            onChange={(e) => setUserFeedback(e.target.value)}
            minRows={1}
            short={true}
            placeholder={`Tell us why?`}
            className="relative left-[-5px] bottom-[-3px] rounded-[4px_4px_10px_10px] resize-none p-2 box-border border-[1.5px] border-app-grey-2 focus:border-app-primary-cta focus:outline-none text-[15px] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            iconButtonProps={showFeedbackSend ? {
              src: "/assets/send.svg",
              onClick: handleFeedbackSubmit,
              disabled: !userFeedback.trim(),
              ariaLabel: "Submit feedback",
              size: 28,
              iconSize: 24,
              variant: !userFeedback.trim() ? "secondary" : "primary",
              className: !userFeedback.trim() ? "[&_path]:fill-app-grey-2 [&_.icon-wrapper]:mt-[3px]" : "[&_path]:fill-app-primary-cta [&_.icon-wrapper]:mt-[3px]",
            } : undefined}
            containerClassName="[&_.flex.absolute]:pointer-events-none [&_.icon-button-wrapper]:pointer-events-auto"
            alwaysShowControls={true}
          />
        </div>
      )}
      <div className="clearfix"></div>
      {isConfirmDeleteModalOpen &&
        createPortal(
          <ConfirmModal
            ref={(node) => {
              setConfirmModal(node);
              confirmModalClickOutsideRef.current = node;
            }}
            style={{
              ...modalStyles.popper,
              zIndex: 20000,
            }}
            {...modalAttributes.popper}
            content="This card will be deleted permanently."
            onCancel={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
          />,
          document.getElementById("card-sets"),
        )}
    </div>
  );
});

export default CardView;
